import { useEffect, useRef, useState } from "react";
import type { BlogPostEntry, BuilderApi, PostRevision } from "../api/contract";
import { postChanges } from "../lib/postChanges";

/**
 * What the owner has changed in a published post since readers last saw it, in plain words. Null until the
 * post's two versions have loaded, and for a post with nothing to update. The words of each version are an
 * immutable revision, so they are read once per digest.
 */
export function usePostChanges(api: BuilderApi, siteId: string, post: Pick<BlogPostEntry, "id" | "working" | "live" | "hasChanges"> | undefined, tagLabel: (id: string) => string): string[] | null {
  const [lines, setLines] = useState<string[] | null>(null);
  const revisions = useRef(new Map<string, PostRevision>());
  const label = useRef(tagLabel);
  label.current = tagLabel;
  const live = post?.live ?? null;
  const working = post?.working;
  const wanted = Boolean(post?.hasChanges && live && working);
  const liveKey = live ? JSON.stringify(live) : "";
  const workingKey = working ? JSON.stringify(working) : "";

  useEffect(() => {
    if (!wanted || !live || !working) { setLines(null); return; }
    let cancelled = false;
    const read = async (digest: string): Promise<PostRevision | null> => {
      const known = revisions.current.get(digest);
      if (known) return known;
      try {
        const loaded = await api.getPostRevision(siteId, digest);
        revisions.current.set(digest, loaded);
        return loaded;
      } catch { return null; }
    };
    void Promise.all([read(live.revision), read(working.revision)]).then(([before, after]) => {
      if (cancelled) return;
      setLines(postChanges({ copy: live, revision: before }, { copy: working, revision: after }, (id) => label.current(id)));
    });
    return () => { cancelled = true; };
    // The copies are compared by value: a reload that returns the same post must not read again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, siteId, wanted, liveKey, workingKey]);

  return wanted ? lines : null;
}
